import React from 'react';
import { NextPage } from 'next';
import Head from 'next/head';
import useSWR from 'swr';
import Link from '@b2b-frontend/components/Link';
import Image from '@b2b-frontend/components/Image';
import renderContentBlock from '@b2b-frontend/components/renderContentBlock';
import { ContentBlock, IImage, IStoryPreview, ITrainer, IBrandBanners } from '@b2b-frontend/types';
import Heading from 'components/Heading';
import Banner from 'components/Banner';
import MaxWidthContainer from 'components/MaxWidthContainer';
import PageHeading from 'components/PageHeading';
import FeatureList from 'components/FeatureList';
import Breadcrumbs from 'components/Breadcrumbs';
import ProductCarousel from 'components/ProductCarousel';
import { scrollElementIntoView } from 'utils/dom';
import useUser from 'utils/useUser';

export interface ILearnBrandProps {
	logo: IImage;
	brand: string;
	brandBanners: IBrandBanners;
	brand_slug: string;
	learn: ContentBlock[];
	stories: IStoryPreview[];
	trainers: ITrainer[];
}

const LearnBrand: NextPage<ILearnBrandProps> = ({
	logo,
	brand,
	brandBanners,
	brand_slug,
	stories,
	trainers,
	learn,
}) => {
	const { isInitialised } = useUser();
	const { data: products = [], isValidating: isFetchingProducts } = useSWR(() =>
		isInitialised
			? `/api/best-sellers${brand_slug ? `/${brand_slug}` : ''}?sort_mode=top_sellers`
			: null,
	);

	const onLinkClicked = event => {
		event.preventDefault();

		scrollElementIntoView(document.getElementById('contact'), 'center');
	};

	const hasBanner = !!brandBanners.learnPageBanner.src;

	return (
		<>
			<Head>
				<title>
					Learn about {brand} | {process.env.appTitle}
				</title>
			</Head>
			<div className="bg-black">
				<MaxWidthContainer className="py-6 flex items-center justify-between">
					<Breadcrumbs dark />
					{trainers.length > 0 && (
						<Link
							href="#contact"
							className="text-brand hover:text-white text-xs"
							linkProps={{
								onClick: onLinkClicked,
							}}
						>
							Contact your trainers
						</Link>
					)}
				</MaxWidthContainer>
			</div>

			{hasBanner ? (
				<Banner image={brandBanners.learnPageBanner} />
			) : (
				<div className="capitalize w-full bg-pattern bg-repeat bg-large flex justify-center items-start text-lg">
					<Heading className="py-20">
						{logo?.src ? <Image image={logo} width={230} height={61} /> : brand}
					</Heading>
				</div>
			)}

			<div className="relative w-full bg-white max-w-[1640px] transform mx-auto mb-20">
				<MaxWidthContainer>
					{learn.map(renderContentBlock)}
					{stories.length > 0 && (
						<>
							<PageHeading>Stories</PageHeading>
							<ul className="grid grid-cols-2 gap-6 sm:grid-cols-3 sm:gap-x-28 gap-y-16 mb-24">
								{stories.map(story => {
									return (
										<li key={story.id}>
											<article className="flex flex-col h-full">
												<Heading
													serif={false}
													size="text-xs"
													className="uppercase mb-3"
												>
													{story.title}
												</Heading>
												<p className="text-base-sm font-light mb-3 break-words">
													{story.previewText}
												</p>
												<Link
													href={`/learn/${brand_slug}/stories/${story.id}`}
													className="text-brand text-xs font-bold uppercase mt-auto"
												>
													{story.ctaText}
												</Link>
											</article>
										</li>
									);
								})}
							</ul>
						</>
					)}
				</MaxWidthContainer>
				<MaxWidthContainer>
					<PageHeading>Best Sellers</PageHeading>
				</MaxWidthContainer>
				<ProductCarousel products={products} isFetching={isFetchingProducts} />
			</div>
			{trainers.length > 0 && (
				<div className="border-t border-gray pb-20" id="contact">
					<MaxWidthContainer>
						<Heading as="h3" className="font-sans mb-3 uppercase" size="text-xs">
							My {brand} trainers
						</Heading>
						<ul className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-[812px]">
							{trainers.map(trainer => (
								<li key={trainer.id}>
									<dl className="text-xs">
										<div className="flex">
											<dt className="mr-2">{trainer.state}:</dt>
											<dd>{trainer.name}</dd>
										</div>
										<div className="flex">
											<dt className="sr-only">Email:</dt>
											<dd>
												<Link href={`mailto:${trainer.email}`}>
													{trainer.email}
												</Link>
											</dd>
										</div>
										<div className="flex">
											<dt className="mr-2">Mob:</dt>
											<dd>
												<Link href={`tel:${trainer.phoneNumber}`}>
													{trainer.phoneNumber}
												</Link>
											</dd>
										</div>
									</dl>
								</li>
							))}
						</ul>
					</MaxWidthContainer>
				</div>
			)}
			<FeatureList />
		</>
	);
};

export default LearnBrand;
